import { styled } from '@mui/material/styles';
import MuiDrawer from '@mui/material/Drawer';
import MuiList from '@mui/material/List';
import MuiListItem from '@mui/material/ListItem';
import MuiListItemButton from '@mui/material/ListItemButton';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MuiMenuItem from '@mui/material/MenuItem';
import { Link } from 'react-router';
import { Typography } from '@components/Typography';

export const SIDEBAR_WIDTH = 280;
export const SIDEBAR_COLLAPSED_WIDTH = 64;
export const SIDEBAR_POPOVER_WIDTH = 240;

export const buttonReset = {
    border: 0,
    background: 'none',
    font: 'inherit',
    textAlign: 'left',
    cursor: 'pointer',
};

const sidebarActionStyles = {
    position: 'absolute',
    right: 8,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 28,
    height: 28,
};

const bookmarkIconStyles = isActive => ({
    '.sidebar-bookmark-icon': {
        color: isActive ? '#fff' : 'rgba(255, 255, 255, 0.74)',
        flexShrink: 0,
    },
    '&:hover .sidebar-bookmark-icon': { color: '#fff' },
    '&& .sidebar-bookmark-icon path': { fill: 'currentColor', fillOpacity: 1 },
    '&& .sidebar-bookmark-outline path': { fill: 'none' },
});

export const Header = styled('span', {
    shouldForwardProp: prop => prop !== 'isCollapsed',
})(({ isCollapsed }) => ({
    width: '100%',
    height: 72,
    display: 'flex',
    justifyContent: isCollapsed ? 'center' : 'space-between',
    alignItems: 'center',
    borderBottom: '1px solid #373737',
    padding: isCollapsed ? '22px 0' : '22px 19px',
    ...(isCollapsed && {
        cursor: 'pointer',
        '& .sidebar-logo': {
            display: 'flex',
        },
        '& .sidebar-expand': {
            display: 'none',
        },
        '&:hover .sidebar-logo': {
            display: 'none',
        },
        '&:hover .sidebar-expand': {
            display: 'flex',
        },
    }),
    '@media (max-width: 1024px)': {
        height: 60,
        padding: 16,
        justifyContent: 'space-between',
    },
}));

export const HeaderActions = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: 24,
});

export const LogoLink = styled(Link)({
    display: 'flex',
    alignItems: 'center',
    textDecoration: 'none',
});

export const IconWrapper = styled('div')({
    height: 14,
    display: 'flex',
    justifyContent: 'center',
    width: 22,
    '& path': {
        fill: '#fff !important',
    },
});

export const PluspointText = styled('span')({
    fontFamily: 'Rubik, sans-serif',
    fontWeight: 500,
    fontSize: '10px',
    lineHeight: '12px',
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    color: '#FFFFFF',
    marginLeft: 11,
});

export const MobileTitle = styled(Typography)({
    flex: 1,
    color: '#FFFFFF',
    marginLeft: 4,
});

export const SidebarDrawer = styled(MuiDrawer, {
    shouldForwardProp: prop => prop !== 'isExpanded' && prop !== 'isCollapsed',
})(({ isExpanded, isCollapsed }) => ({
    '.MuiDrawer-paper': {
        gridArea: 'sidebar',
        backgroundColor: '#272727',
        width: isCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH,
        top: 'var(--app-top-inset, 0px)',
        height: 'calc(100% - var(--app-top-inset, 0px))',
        display: 'flex',
        flexDirection: 'column',
        transition: 'width 0.2s ease',
        overflowX: 'hidden',
        '@media (max-width: 1024px)': {
            width: '100%',
            height: !isExpanded ? 60 : 'calc(100% - var(--app-top-inset, 0px))',
            overflow: 'hidden',
            transition: 'all 0.2s',
        },
        '@media print': {
            display: 'none',
        },
    },
    '& .MuiPaper-root': {
        zIndex: 5,
    },
}));

export const List = styled(MuiList, {
    shouldForwardProp: prop => prop !== 'isExpanded' && prop !== 'isMobile' && prop !== 'isCollapsed',
})(({ isExpanded, isMobile, isCollapsed }) => ({
    paddingTop: isExpanded && isMobile ? 16 : isCollapsed ? 4 : 0,
    paddingBottom: 0,
    display: isMobile && !isExpanded ? 'none' : isCollapsed ? 'flex' : 'block',
    ...(isCollapsed && { flexDirection: 'column' }),
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
}));

export const Footer = styled('div')({
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    padding: '8px 0',
    borderTop: '1px solid #373737',
});

export const FooterRow = styled('button', {
    shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    width: '100%',
    minHeight: 48,
    padding: '8px 16px 8px 24px',
    ...buttonReset,
    background: isActive ? '#2E2E2E' : 'transparent',
    color: isActive ? '#fff' : 'rgba(255, 255, 255, 0.74)',
    position: 'relative',
    '.MuiTypography-body1': {
        flex: 1,
        minWidth: 0,
        color: 'inherit',
        fontWeight: isActive ? 500 : 400,
    },
    '& > svg': {
        width: 20,
        height: 20,
        flexShrink: 0,
    },
    '& > svg path': {
        fill: 'currentColor',
        fillOpacity: 1,
    },
    '.sidebar-footer-arrow': {
        width: 18,
        height: 18,
        color: 'rgba(255, 255, 255, 0.5)',
    },
    '& > .sidebar-active-indicator': {
        width: 8,
        height: '100%',
        position: 'absolute',
        left: 0,
        top: 0,
        backgroundColor: isActive ? 'var(--primary-main)' : 'transparent',
    },
    '&:hover, &:focus-visible': {
        color: '#fff',
        '.sidebar-footer-arrow': { color: '#fff' },
    },
    '&:focus-visible': {
        outline: '2px solid rgba(255, 255, 255, 0.5)',
        outlineOffset: -2,
    },
}));

export const CollapsedBell = styled('span')({
    position: 'relative',
    display: 'flex',
    '& > svg': {
        width: 20,
        height: 20,
    },
});

export const FooterCount = styled('span')({
    marginLeft: 'auto',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: 'Rubik, sans-serif',
    fontSize: 11,
    lineHeight: 1,
    fontWeight: 500,
    height: 18,
    minWidth: 18,
    padding: '0 5px',
    borderRadius: 9,
    color: '#fff',
    backgroundColor: 'var(--error-main)',
    flexShrink: 0,
});

const listItemOptions = {
    shouldForwardProp: prop => prop !== 'isActive' && prop !== 'isExpanded',
};

const listItemStyles = ({ isActive, isExpanded = false, type }) => ({
    ...bookmarkIconStyles(isActive),
    padding: '8px 16px 8px 24px',
    ...(type === 'multi' && { paddingRight: 44 }),
    color: 'var(--text-primary)',
    display: 'flex',
    transition: 'all 0.3s ease-out',
    position: 'relative',
    backgroundColor: isActive ? '#2E2E2E' : 'transparent',
    '.MuiTypography-body1': {
        color: isActive ? '#fff' : 'rgba(255, 255, 255, 0.74)',
        marginLeft: '16px',
        fontWeight: isActive ? 500 : 400,
    },
    '.MuiTypography-body2': {
        marginLeft: 41,
        color: isActive ? '#fff' : 'rgba(255, 255, 255, 0.74)',
        fontWeight: isActive ? 500 : 400,
    },
    svg: {
        path: {
            fill: isActive ? '#fff' : 'rgba(255, 255, 255, 0.74)',
            fillOpacity: isActive ? 1 : 0.74,
        },
    },
    '& > .sidebar-active-indicator': {
        width: 8,
        height: '100%',
        position: 'absolute',
        left: 0,
        ...(type === 'single' && {
            backgroundColor: !isActive ? 'transparent' : 'var(--primary-main)',
        }),
        ...(type === 'multi' && {
            backgroundColor: !isActive ? 'transparent' : isExpanded ? '#373737' : 'var(--primary-main)',
        }),
    },
    '&:hover': {
        '.MuiTypography-body1, .MuiTypography-body2': {
            color: '#fff',
        },
        svg: {
            path: {
                fill: '#fff',
                fillOpacity: 1,
            },
        },
    },
    '.sidebar-menu-arrow': {
        ...sidebarActionStyles,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        svg: {
            width: 18,
            height: 18,
        },
    },
});

export const ListItem = styled(MuiListItem, listItemOptions)(listItemStyles);

export const ListItemButton = styled(MuiListItemButton, listItemOptions)(listItemStyles);

export const CollapsedListItem = styled('div', {
    shouldForwardProp: prop => prop !== 'isActive' && prop !== 'isNarrow' && prop !== 'as',
})(({ isActive, isNarrow }) => ({
    ...bookmarkIconStyles(isActive),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    width: isNarrow ? 40 : SIDEBAR_COLLAPSED_WIDTH,
    height: 40,
    ...(isNarrow && { margin: '0 auto', borderRadius: '50%' }),
    ...buttonReset,
    padding: 0,
    textDecoration: 'none',
    backgroundColor: isActive ? '#2E2E2E' : 'transparent',
    '&::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: 0,
        width: 3,
        height: '100%',
        backgroundColor: isActive ? 'var(--primary-main)' : 'transparent',
    },
    svg: {
        path: {
            fill: isActive ? '#fff' : 'rgba(255, 255, 255, 0.74)',
            fillOpacity: isActive ? 1 : 0.74,
        },
    },
    '&:hover': {
        svg: {
            path: {
                fill: '#fff',
                fillOpacity: 1,
            },
        },
    },
    '&[aria-disabled="true"]': {
        cursor: 'default',
    },
}));

export const PopoverHeader = styled('div')({
    padding: '8px 16px 4px',
    fontFamily: 'Rubik, sans-serif',
    color: 'rgba(255, 255, 255, 0.5)',
    fontSize: 13,
    fontWeight: 500,
    lineHeight: '20px',
});

export const PopoverItem = styled('div', {
    shouldForwardProp: prop => prop !== 'isActive',
})(({ isActive }) => ({
    ...bookmarkIconStyles(isActive),
    padding: '4px 16px',
    cursor: 'pointer',
    minHeight: 32,
    display: 'flex',
    alignItems: 'center',
    backgroundColor: isActive ? 'rgba(255, 255, 255, 0.08)' : 'transparent',
    '.MuiTypography-body2': {
        color: isActive ? '#fff' : 'rgba(255, 255, 255, 0.74)',
        fontWeight: isActive ? 500 : 400,
        lineHeight: '24px',
        marginLeft: 0,
    },
    '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
        '.MuiTypography-body2': {
            color: '#fff',
        },
    },
}));

export const SidebarPopover = styled(Paper)({
    backgroundColor: '#272727',
    borderRadius: 8,
    padding: '8px 0',
    width: SIDEBAR_POPOVER_WIDTH,
    maxHeight: 'calc(100vh - 16px)',
    overflowY: 'auto',
    boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.4)',
});

export const BookmarkButton = styled(IconButton)({
    ...sidebarActionStyles,
    color: 'rgba(255, 255, 255, 0.74)',
    opacity: 0,
    transition: 'opacity 150ms ease',
    '.sidebar-navigation-row:hover &, .sidebar-navigation-row a:focus-visible + &, &:focus-visible': {
        opacity: 1,
    },
    '&:hover, &:focus-visible': {
        color: '#fff',
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
    '&:focus-visible': {
        outline: '2px solid currentColor',
    },
    '&[aria-disabled="true"]': {
        cursor: 'default',
        color: 'rgba(255, 255, 255, 0.5)',
    },
    '&& .sidebar-bookmark-icon': {
        width: 16,
        height: 16,
        color: 'inherit',
    },
    '& .sidebar-bookmark-filled': { display: 'none' },
    '&[aria-pressed="true"] .sidebar-bookmark-filled': { display: 'block' },
    '&[aria-pressed="true"] .sidebar-bookmark-outline': { display: 'none' },
    '@media (hover: hover)': {
        '&:hover .sidebar-bookmark-filled': { display: 'block' },
        '&:hover .sidebar-bookmark-outline': { display: 'none' },
        '&[aria-pressed="true"]:hover .sidebar-bookmark-filled': { display: 'none' },
        '&[aria-pressed="true"]:hover .sidebar-bookmark-outline': { display: 'block' },
    },
    '@media (hover: none)': {
        opacity: 1,
    },
});

export const CollapseToggle = styled('button')({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    borderRadius: 6,
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    color: 'rgba(255, 255, 255, 0.5)',
    padding: 0,
    '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
        color: '#fff',
    },
    '@media (max-width: 1024px)': {
        display: 'none',
    },
});

export const ProfileMenuPopover = styled(Menu)({
    '.MuiMenu-paper': {
        backgroundColor: '#272727',
        borderRadius: 8,
        width: SIDEBAR_POPOVER_WIDTH,
        boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.4)',
        marginTop: -8,
        marginLeft: 16,
    },
    '.MuiMenu-list': {
        padding: '8px 0',
    },
});

export const ProfileMenuItem = styled(MuiMenuItem, {
    shouldForwardProp: prop => prop !== 'withIcon',
})(({ withIcon }) => ({
    gap: 16,
    padding: withIcon ? '8px 16px' : '4px 16px',
    minHeight: withIcon ? 40 : 32,
    color: 'rgba(255, 255, 255, 0.74)',
    '.MuiTypography-body2': {
        color: 'inherit',
        lineHeight: '24px',
    },
    svg: {
        width: 18,
        height: 18,
        flexShrink: 0,
        path: {
            fill: 'currentColor',
            fillOpacity: 1,
        },
    },
    '&:hover, &.Mui-focusVisible, &.Mui-selected, &.Mui-selected:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
        color: '#fff',
    },
    '&.Mui-selected .MuiTypography-body2': {
        fontWeight: 500,
    },
}));

export const ProfileMenuDivider = styled('hr')({
    border: 0,
    borderTop: '1px solid #373737',
    margin: '8px 0',
});
